import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AssetBookingSummary } from '../../../api/asset/assetApiTypes';
import { makeRentalBookingDetailsUrl } from '../../../routes';
import AssetDetailSection from './AssetDetailSection';
import { STATION_TIME_ZONE } from '../../../utils/stationTime';

interface RecentBookingsSectionProps {
    bookings: AssetBookingSummary[];
    mode: 'view' | 'edit' | 'create';
    /** #3219: the zone of the object's location; the station default when omitted. */
    timeZone?: string;
}

const statusColors: Record<string, string> = {
    booked: 'bg-green-100 text-green-800',
    active: 'bg-blue-100 text-blue-800',
    finished: 'bg-gray-100 text-gray-800',
    canceled: 'bg-red-100 text-red-800',
    reserved: 'bg-purple-100 text-purple-800',
    incompleted: 'bg-amber-100 text-amber-800',
};

const RecentBookingsSection: React.FC<RecentBookingsSectionProps> = ({ bookings, timeZone = STATION_TIME_ZONE }) => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.language.startsWith('de') ? 'de-DE' : 'en-US';

    const formatDate = (dateStr: string): string => {
        try {
            return new Date(dateStr).toLocaleDateString(dateLocale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone,
            });
        } catch {
            return dateStr;
        }
    };

    const hasBookings = bookings && bookings.length > 0;

    return (
        <AssetDetailSection title={t('asset_detail.sections.recent_bookings')}>
            {!hasBookings ? (
                <p className={'text-sm text-foreground/40 italic'}>
                    {t('asset_detail.empty_states.recent_bookings')}
                </p>
            ) : (
                <div className={'overflow-x-auto'}>
                    <table className={'w-full text-sm'}>
                        <thead>
                            <tr className={'border-b border-foreground/10'}>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('asset_detail.fields.booking_number')}
                                </th>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('asset_detail.fields.status')}
                                </th>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('asset_detail.fields.period')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookings.map((booking) => (
                                <tr key={booking.orderId} className={'border-b border-foreground/5'}>
                                    <td className={'py-2'}>
                                        <Link
                                            to={makeRentalBookingDetailsUrl(booking.orderId)}
                                            className={'text-primary hover:underline'}
                                        >
                                            {booking.bookingNumber}
                                        </Link>
                                    </td>
                                    <td className={'py-2'}>
                                        <span
                                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                statusColors[booking.status] ?? 'bg-gray-100 text-gray-800'
                                            }`}
                                        >
                                            {t(`dashboard.assets_availability.status.${booking.status}`)}
                                        </span>
                                    </td>
                                    <td className={'py-2 text-foreground/80'}>
                                        {formatDate(booking.startDate)} – {formatDate(booking.endDate)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </AssetDetailSection>
    );
};

export default RecentBookingsSection;
